import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { log } from '@app/shared/utils/logger';
import { Modal } from '@app/shared/ui/organisms/Common';
import {
  Heading,
  Paragraph,
  Button,
  FlyingCookie,
  DailyRewardDayCard,
  type DailyRewardDayCardProps,
} from '@app/shared/ui/atoms/Common';
import { DAY_TO_REWARD_MAP } from '@app/shared/constants/common';
import { StreakDay } from '@app/shared/types/common';
import { FlyingCookieCoordinateTransition } from '@app/shared/types/flyingCookie';
import { eatingPandaImage, cookieImage } from '@app/assets';

interface DailyRewardModalProps {
  isOpen: boolean;
  streak: number;
  isClaimingReward: boolean;
  flyingCookie: FlyingCookieCoordinateTransition | null;
  isCloseWindow?: boolean;
  onRewardAccept: (event: React.MouseEvent<HTMLElement>, reward: number) => void;
}

const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

const getCardVariantForDay = (day: number, streak: number): DailyRewardDayCardProps['variant'] => {
  if (day < streak) {
    return 'accepted';
  } else if (day === streak) {
    return 'current';
  }
  return 'default';
};

export function DailyRewardModal({
  isOpen,
  streak,
  isClaimingReward,
  flyingCookie,
  onRewardAccept,
}: DailyRewardModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    void log({
      level: 'warn',
      message: 'DailyRewardModal',
      extra: { isOpen, streak, isClaimingReward },
    });
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        featuredImg={<img src={eatingPandaImage} alt="eating panda" className="w-[134px]" />}
        displayCloseButton={false}
        hiddenTitle="Daily reward"
      >
        <Heading className="text-foreground">{t('daily_reward.title')}</Heading>
        <Paragraph className="text-foreground">{t('daily_reward.description')}</Paragraph>

        <ul className="mt-2 flex flex-wrap justify-center gap-3">
          {DAYS.map((day) => (
            <DailyRewardDayCard
              key={day}
              day={day}
              variant={getCardVariantForDay(day, streak)}
              onClick={day === streak && !isClaimingReward ? onRewardAccept : undefined}
              className="w-[calc((100%-36px)/4)]"
            />
          ))}
        </ul>

        <Button
          className="flex animate-pulse-beat items-center justify-center gap-2"
          disabled={isClaimingReward}
          onClick={(e) => {
            onRewardAccept(e, DAY_TO_REWARD_MAP[streak as StreakDay]);
          }}
        >
          {isClaimingReward ? t('common.wait') : t('daily_reward.take')}
          <img src={cookieImage} alt="cookie" className="w-5" />
        </Button>
      </Modal>

      {flyingCookie && (
        <FlyingCookie
          startX={flyingCookie.startX}
          startY={flyingCookie.startY}
          moveToX={flyingCookie.moveToX}
          moveToY={flyingCookie.moveToY}
          size="lg"
        />
      )}
    </>
  );
}
